import { cn } from 'lib/utils'
import {
  ChevronDown,
  Download,
  FastForward,
  Loader2,
  Pause,
  Play,
  Rewind,
  Volume2,
  VolumeX,
} from 'lucide-react'
import { Button } from '../button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '../dropdown-menu'

interface AudioControlsProps {
  playing: boolean
  muted: boolean
  downloading: boolean
  playbackRate: number
  onPlay: () => void
  onRewind: () => void
  onForward: () => void
  onMute: () => void
  onDownload: () => void
  onPlaybackRate: (rate: number) => void
}

const SkipButton = ({
  direction,
  onClick,
}: {
  direction: 'back' | 'forward'
  onClick: () => void
}) => {
  const SkipIcon = direction === 'back' ? Rewind : FastForward
  const label = direction === 'back' ? 'Rewind 10 seconds' : 'Forward 10 seconds'

  return (
    <Button
      type="button"
      variant="ghost"
      size="iconSm"
      className="shrink-0 rounded-md p-0 text-secondary hover:bg-hv1 hover:text-primary"
      aria-label={label}
      tooltipContent={label}
      data-testid={`button-audio-skip-${direction}`}
      onClick={onClick}
    >
      <SkipIcon size={17} strokeWidth={1.75} aria-hidden="true" />
    </Button>
  )
}

export const AudioControls = ({
  playing,
  muted,
  downloading,
  playbackRate,
  onPlay,
  onRewind,
  onForward,
  onMute,
  onDownload,
  onPlaybackRate,
}: AudioControlsProps) => {
  const PlaybackIcon = playing ? Pause : Play
  const VolumeIcon = muted ? VolumeX : Volume2
  const playLabel = playing ? 'Pause audio' : 'Play audio'
  const muteLabel = muted ? 'Unmute audio' : 'Mute audio'

  return (
    <div className="inline-flex max-w-full flex-wrap items-center gap-0.5 rounded-lg border border-border bg-background p-1 text-secondary">
      <Button
        type="button"
        size="iconSm"
        className="shrink-0 rounded-md bg-brand p-0 text-brand-foreground hover:bg-brand hover:text-brand-foreground focus-visible:ring-brand"
        aria-label={playLabel}
        tooltipContent={playLabel}
        data-testid="button-audio-play-pause"
        onClick={onPlay}
      >
        <PlaybackIcon size={15} strokeWidth={1.75} className="fill-current" aria-hidden="true" />
      </Button>
      <div className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
      <SkipButton direction="back" onClick={onRewind} />
      <SkipButton direction="forward" onClick={onForward} />
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className={cn(
          'shrink-0 rounded-md p-0 text-secondary hover:bg-hv1 hover:text-primary',
          muted && 'bg-accent text-accent-foreground',
        )}
        aria-label={muteLabel}
        aria-pressed={muted}
        tooltipContent={muteLabel}
        data-testid="button-audio-mute"
        onClick={onMute}
      >
        <VolumeIcon size={17} strokeWidth={1.75} aria-hidden="true" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="iconSm"
        className="shrink-0 rounded-md p-0 text-secondary hover:bg-hv1 hover:text-primary"
        aria-label={downloading ? 'Downloading audio' : 'Download audio'}
        aria-busy={downloading}
        tooltipContent="Download audio"
        data-testid="button-audio-download"
        disabled={downloading}
        onClick={onDownload}
      >
        {downloading ? (
          <Loader2 size={17} strokeWidth={1.75} className="animate-spin" aria-hidden="true" />
        ) : (
          <Download size={17} strokeWidth={1.75} aria-hidden="true" />
        )}
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            className="h-8 min-w-12 gap-1 rounded-md px-2 font-jetbrains text-xs-400 text-secondary normal-case tracking-normal tabular-nums hover:bg-hv1 hover:text-primary"
            aria-label={`Playback speed: ${playbackRate}x`}
            data-testid="button-audio-playback-speed"
          >
            <span>{playbackRate}x</span>
            <ChevronDown size={13} strokeWidth={1.75} aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="min-w-24">
          <DropdownMenuRadioGroup
            value={String(playbackRate)}
            onValueChange={(value) => onPlaybackRate(Number(value))}
            aria-label="Playback speed"
          >
            {[0.75, 1, 1.5, 2].map((rate) => (
              <DropdownMenuRadioItem
                key={rate}
                value={String(rate)}
                className="font-jetbrains text-xs-400 tabular-nums data-[state=checked]:bg-accent"
                data-testid={`option-audio-speed-${rate}`}
              >
                {rate}x
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
